import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppContext, useShowsApi } from '@/context/AppContext';

import { useShowData } from '../hooks/useShowData';
import { useHoldSelection } from '../hooks/useHoldSelection';
import { useInlineEditing } from '../hooks/useInlineEditing';
import { useHoldOperations } from '../hooks/useHoldOperations';
import { useBulkOperations } from '../hooks/useBulkOperations';
import { useExpenseCopyDialog } from '../hooks/useExpenseCopyDialog';

import { ShowDetailsHeader } from './ShowDetails/ShowDetailsHeader';
import { ShowInfoMessages } from './ShowDetails/ShowInfoMessages';

import { ShowDatesPerformanceCard } from './ShowDetails/holds/ShowDatesPerformanceCard';
import { HoldDecisionAlert } from './ShowDetails/holds/HoldDecisionAlert';

import { ShowDateDetails } from './ShowDetails/ShowDateDetails';

import { ShowNotesCard } from './ShowDetails/notes/ShowNotesCard';
import { ShowDangerZone } from './ShowDetails/danger/ShowDangerZone';
import { ShowActionsBar } from './ShowDetails/ShowActionsBar';

import { ShowStageManager } from './ShowStageManager';
import { ShowPerformerManager } from './ShowPerformerManager';

import { DeleteShowDialog } from './ShowDetails/dialogs/DeleteShowDialog';
import { CopyExpensesDialog } from './ShowDetails/dialogs/CopyExpensesDialog';

import { ConfirmChallengeDialog } from './ConfirmChallengeDialog';
import { ChallengeResponseDialog } from '../../bookings/components/ChallengeResponseDialog';
import { ShowWorkflowTracker } from './ShowWorkflowTracker';

import type { HoldList } from 'api-client/models';
import type { ExpenseItem } from '../types/ExpenseItem';
import type { TicketRevenueItem } from '../types/TicketRevenueItem';

export const ShowDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppContext();
  const showsApi = useShowsApi();

  const {
    show,
    holds,
    loading,
    holdsLoading,
    error,
    infoMessage,
    setInfoMessage,
    refetch,
  } = useShowData(id);

  const {
    selectedHoldIds,
    selectedHoldForDetails,
    handleSelectHold,
    handleSelectAllHolds,
    handleSelectHoldForDetails,
  } = useHoldSelection(holds, id);

  const [internalError, setInternalError] = useState<string | null>(null);
  const {
    editFormData,
    fullHoldDetails,
    fetchFullHoldDetails,
    editingSection,
    startEditing,
    cancelEditing,
    saveChanges,
    updateFormField,
  } = useInlineEditing(setInfoMessage, setInternalError, {
    showId: id, holds, selectedHoldId: selectedHoldForDetails?.id,
  });

  const {
    confirmOrChallengeHolds,
    releaseHoldsBatch,
    respondToChallenge,
  } = useHoldOperations(setInfoMessage, setInternalError, refetch);

  const { copyTimesToAllDates, copyExpensesToAllDates } = useBulkOperations(
    setInfoMessage,
    setInternalError,
    refetch,
    { showId: id, holds },
  );

  // Auto-fetch full hold details when selectedHoldForDetails changes
  useEffect(() => {
    if (selectedHoldForDetails && !fullHoldDetails[selectedHoldForDetails.id]) {
      void fetchFullHoldDetails(selectedHoldForDetails.id);
    }
  }, [selectedHoldForDetails, fetchFullHoldDetails]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [challengeResponseDialogOpen, setChallengeResponseDialogOpen] = useState(false);
  const [selectedChallengedHold, setSelectedChallengedHold] = useState<HoldList | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletingShow, setIsDeletingShow] = useState(false);
  const selectedExpenseItems = selectedHoldForDetails
    ? fullHoldDetails[selectedHoldForDetails.id]?.expenseItems || [] : [];
  const copyDialog = useExpenseCopyDialog({
    showId: id, selectedHold: selectedHoldForDetails, holds, formData: { expenseItems: selectedExpenseItems },
    fetchDetails: fetchFullHoldDetails, copyExpenses: copyExpensesToAllDates, refetch, setError: setInternalError,
  });

  // Dialog handlers
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleConfirmFromDialog = async (holdIds: string[]) => {
    await confirmOrChallengeHolds(holdIds, holds);
    setDialogOpen(false);
  };

  const handleOpenChallengeResponseDialog = (hold: HoldList) => {
    setSelectedChallengedHold(hold);
    setChallengeResponseDialogOpen(true);
  };

  const handleCloseChallengeResponseDialog = () => {
    setChallengeResponseDialogOpen(false);
    setSelectedChallengedHold(null);
  };

  const handleConfirmChallengedHold = async () => {
    if (selectedChallengedHold) {
      await respondToChallenge(selectedChallengedHold.id, 'confirm');
    }
  };

  const handleReleaseChallengedHold = async () => {
    if (selectedChallengedHold) {
      await respondToChallenge(selectedChallengedHold.id, 'release');
    }
  };

  const handleDeleteShow = async () => {
    if (!id || isDeletingShow) return;

    try {
      setIsDeletingShow(true);
      await showsApi.showsDestroy({ id });
      setDeleteDialogOpen(false);
      navigate('/shows', { state: { message: 'Show deleted successfully' } });
    } catch (err) {
      setInternalError(err instanceof Error ? err.message : 'Failed to delete show');
    } finally {
      setIsDeletingShow(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && (!show || holds.length === 0)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/shows')}>
          Back to Shows
        </Button>
      </Box>
    );
  }

  // No show found
  if (!show) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Show not found</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/shows')}>
          Back to Shows
        </Button>
      </Box>
    );
  }

  const confirmedHolds = holds.filter((h) => h.status === 'confirmed');
  const otherConfirmedHoldCount = selectedHoldForDetails
    ? confirmedHolds.filter((h) => h.id !== selectedHoldForDetails.id).length
    : 0;

  // Wrapper functions to adapt hook signatures to component interfaces
  const handleSaveChanges = async (section: string) => {
    if (!show || !selectedHoldForDetails) return;
    await saveChanges(section, show.id, selectedHoldForDetails, refetch);
  };

  const handleUpdateField = (field: string, value: unknown) => {
    updateFormField(field, value as string | number | null | TicketRevenueItem[] | ExpenseItem[]);
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
      {/* Header with breadcrumbs and title */}
      <ShowDetailsHeader
        show={show}
        onUpdate={async (data) => {
          await showsApi.showsPartialUpdate({
            id: show.id,
            patchedShowCreateUpdate: {
              title: data.title,
              showType: data.showType,
              performerDealType: data.performerDealType ?? null,
              doorDealPercentageOwner: data.doorDealPercentageOwner ?? null,
              doorDealPercentage: data.doorDealPercentage ?? null,
            },
          });
          await refetch();
          setInfoMessage('Show updated successfully');
        }}
      />

      {/* Info/Error Messages */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <ShowInfoMessages
        infoMessage={infoMessage}
        errorMessage={internalError}
        onClearInfo={() => setInfoMessage(null)}
        onClearError={() => setInternalError(null)}
      />

      <ShowWorkflowTracker show={show} holds={holds} />

      {/* Hold Management Section - Unified Card */}
      {holds.length > 0 && (
        <>
          <ShowDatesPerformanceCard
            show={show}
            holds={holds}
            holdsLoading={holdsLoading}
            selectedHoldIds={selectedHoldIds}
            selectedHoldForDetails={selectedHoldForDetails}
            fullHoldDetails={fullHoldDetails}
            onSelectHold={handleSelectHold}
            onSelectAllHolds={() => handleSelectAllHolds(holds)}
            onSelectHoldForDetails={async (hold) => {
              handleSelectHoldForDetails(hold);
            }}
            onConfirmChallenge={() => setDialogOpen(true)}
            onReleaseHolds={async () => {
              await releaseHoldsBatch(selectedHoldIds, holds);
            }}
            onRefresh={refetch}
            onOpenChallengeResponse={handleOpenChallengeResponseDialog}
            getConfirmChallengeButtonText={() => {
              const selectedHolds = holds.filter((h) => selectedHoldIds.has(h.id));
              if (selectedHolds.length === 0) return 'Select Dates';
              const firstPositionHolds = selectedHolds.filter((h) => h.holdPosition === 1);
              const higherPositionHolds = selectedHolds.filter((h) => (h.holdPosition || 0) > 1);
              if (firstPositionHolds.length > 0 && higherPositionHolds.length === 0) {
                return `Confirm ${selectedHolds.length} Date${selectedHolds.length > 1 ? 's' : ''}`;
              } else if (higherPositionHolds.length > 0 && firstPositionHolds.length === 0) {
                return `Challenge ${selectedHolds.length} Date${selectedHolds.length > 1 ? 's' : ''}`;
              } else {
                return `Confirm/Challenge ${selectedHolds.length} Date${selectedHolds.length > 1 ? 's' : ''}`;
              }
            }}
          />

          <HoldDecisionAlert holds={holds} />
        </>
      )}

      {/* Date Details Section - Only shown when a hold is selected */}
      {selectedHoldForDetails && (
        <ShowDateDetails
          show={show}
          hold={selectedHoldForDetails}
          holdDetails={fullHoldDetails[selectedHoldForDetails.id] || null}
          editingSection={editingSection}
          formData={editFormData}
          onStartEdit={startEditing}
          onSave={handleSaveChanges}
          onCancel={cancelEditing}
          onUpdateField={handleUpdateField}
          onCopyTimes={() => copyTimesToAllDates(selectedHoldForDetails, holds, editFormData)}
          onCopyExpenses={copyDialog.onOpen}
          otherConfirmedHoldCount={otherConfirmedHoldCount}
          hasPendingHolds={holds.some((h) => h.status === 'pending')}
          userTimezone={user?.timezone || 'UTC'}
        />
      )}

      {/* Stage Management - Uses existing component from original */}
      <Box sx={{ mt: 3 }}>
        <ShowStageManager
          showId={show.id}
          stages={show.showStages || []}
          onUpdate={refetch}
        />
      </Box>

      {/* Performer Management - Uses existing component from original */}
      <Box sx={{ mt: 3 }}>
        <ShowPerformerManager
          showId={show.id}
          performers={show.showPerformers || []}
          showDealType={show.performerDealType}
          showDoorDealPercentageOwner={show.doorDealPercentageOwner}
          showDoorDealPercentage={show.doorDealPercentage}
          onUpdate={refetch}
        />
      </Box>

      {(show.notes || show.settlementNotes) && (
        <Box sx={{ mt: 3 }}>
          <ShowNotesCard notes={show.notes} settlementNotes={show.settlementNotes} />
        </Box>
      )}

      {/* Danger Zone */}
      <Box sx={{ mt: 3 }}>
        <ShowDangerZone
          showId={show.id}
          showTitle={show.title}
          holdCount={holds.length}
          onDelete={() => { setInternalError(null); setDeleteDialogOpen(true); }}
        />
      </Box>

      {/* Action Bar */}
      <Box sx={{ mt: 3 }}>
        <ShowActionsBar onBack={() => navigate('/shows')} />
      </Box>

      {/* Dialogs */}
      <ConfirmChallengeDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        selectedHolds={holds.filter((h) => selectedHoldIds.has(h.id))}
        allHolds={holds}
        onConfirm={handleConfirmFromDialog}
      />

      {selectedChallengedHold && (
        <ChallengeResponseDialog
          open={challengeResponseDialogOpen}
          onClose={handleCloseChallengeResponseDialog}
          hold={selectedChallengedHold}
          onConfirm={handleConfirmChallengedHold}
          onRelease={handleReleaseChallengedHold}
        />
      )}

      <DeleteShowDialog
        open={deleteDialogOpen}
        showTitle={show.title}
        holdCount={holds.length}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteShow}
        isDeleting={isDeletingShow}
        errorMessage={internalError}
      />

      <CopyExpensesDialog
        open={copyDialog.open}
        expenseItems={selectedExpenseItems}
        targetDateCount={copyDialog.targetDateCount}
        errorMessage={copyDialog.errorMessage}
        refreshOnly={copyDialog.refreshOnly}
        onClose={copyDialog.onClose}
        onConfirm={copyDialog.onConfirm}
      />
    </Box>
  );
};
